

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { SpanishTooltip } from './charts/SpanishTooltip';
import { Icon } from '@iconify/react';
import { HistoricalStatistics, PermanentStatistics } from '../features/surveys/types';

interface SurveyChartsProps {
  data: HistoricalStatistics | PermanentStatistics;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function EmptyStatisticsMessage() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 bg-card border border-border rounded-lg">
      <Icon icon="mdi:chart-box-outline" className="w-16 h-16 text-muted-foreground/50 mb-4" />
      <h3 className="text-lg font-semibold text-foreground mb-2">Sin datos para mostrar</h3>
      <p className="text-muted-foreground text-center max-w-md">
        Aún no hay respuestas en esta encuesta. Las estadísticas y gráficos se mostrarán cuando existan respuestas cargadas.
      </p>
    </div>
  );
}

export function SurveyCharts({ data }: SurveyChartsProps) {
  // Encuesta periódica: sin períodos en el rango de fechas, o sin respuestas con puntaje
  if (data.type === 'periodic') {
    const periods = data.periods ?? [];
    const hasData = periods.length > 0 && periods.some((p) => p.responseCount > 0);
    if (!hasData) {
      if (periods.length === 0) {
        return (
          <div className="flex flex-col items-center justify-center py-16 px-6 bg-card border border-border rounded-lg">
            <Icon icon="mdi:calendar-clock" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Sin respuestas en el período seleccionado</h3>
            <p className="text-muted-foreground text-center max-w-md">
              El histórico solo incluye respuestas dentro de los años elegidos arriba. Si tus respuestas son más antiguas,
              amplía el rango (por ejemplo a 5 años) o vuelve a cargar la página.
            </p>
          </div>
        );
      }
      return <EmptyStatisticsMessage />;
    }
    const historicalData = data.periods.map((p) => ({
      name: p.period,
      puntaje: parseFloat(p.averageWeightedScore.toFixed(2)),
      respuestas: p.responseCount,
      ...p.groupScores.reduce((acc, gs) => ({
        ...acc,
        [gs.name]: parseFloat(gs.weightedScore.toFixed(2)),
      }), {}),
    }));

    return (
      <div className="space-y-8">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-6">Evolución de Puntaje Ponderado</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historicalData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
                <SpanishTooltip itemStyle={{ fontSize: '12px' }} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="puntaje"
                  name="Puntaje Total"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-6">Respuestas por Período</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <SpanishTooltip />
                <Bar dataKey="respuestas" name="Cantidad de Respuestas" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-6">Desglose por Áreas (Evolutivo)</h3>
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historicalData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 5]} />
                <SpanishTooltip />
                <Legend />
                {historicalData.length > 0 && Object.keys(historicalData[0])
                  .filter(key => key !== 'name' && key !== 'puntaje' && key !== 'respuestas')
                  .map((groupName, index) => (
                    <Bar 
                      key={groupName} 
                      dataKey={groupName} 
                      name={groupName} 
                      fill={COLORS[index % COLORS.length]} 
                      radius={[4, 4, 0, 0]}
                    />
                  ))
                }
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  // Encuesta permanente sin respuestas
  if (data.type === 'permanent' && data.totalResponses === 0) {
    return <EmptyStatisticsMessage />;
  }

  // Respuestas sí hay, pero sin áreas configuradas (o sin datos agrupables): mostrar resumen mínimo
  if (data.type === 'permanent' && (!data.groupAverages || data.groupAverages.length === 0)) {
    return (
      <div className="space-y-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col items-center justify-center text-center">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Total de respuestas</h3>
          <div className="text-5xl font-bold text-green-600 mb-2">{data.totalResponses}</div>
          <p className="text-sm text-muted-foreground max-w-md">
            Configura agrupaciones de campos y asigna campos a cada área para ver puntajes y gráficos por gestión.
          </p>
        </div>
      </div>
    );
  }

  // Gráficos para encuestas permanentes (estado)
  const pieData = data.groupAverages.map((ga, index) => ({
    name: ga.name,
    value: parseFloat(ga.weightedScore.toFixed(2)),
    average: parseFloat(ga.average.toFixed(2)),
    color: COLORS[index % COLORS.length],
  }));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col items-center justify-center text-center">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Puntaje Promedio Ponderado</h3>
          <div className="text-5xl font-bold text-primary mb-2">
            {data.averageWeightedScore.toFixed(2)}
          </div>
          <div className="text-sm text-muted-foreground">Sobre un máximo de 5.00</div>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col items-center justify-center text-center">
          <h3 className="text-sm font-medium text-muted-foreground mb-2">Total de Respuestas</h3>
          <div className="text-5xl font-bold text-green-600 mb-2">
            {data.totalResponses}
          </div>
          <div className="text-sm text-muted-foreground">Respuestas válidas procesadas</div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-6">Distribución por Áreas / Gestiones (Estado)</h3>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value.toFixed(2)}`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <SpanishTooltip
                formatter={(value, _name, item) => {
                  const n = typeof value === 'number' ? value : 0;
                  const avg = (item?.payload as { average?: number })?.average ?? 0;
                  return [`${n.toFixed(2)} (Promedio: ${avg.toFixed(2)})`, 'Puntaje Ponderado'];
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-6">Comparación por Áreas / Gestiones</h3>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={data.groupAverages.map(ga => ({
                name: ga.name,
                valor: parseFloat(ga.weightedScore.toFixed(2)),
                promedio: parseFloat(ga.average.toFixed(2))
              }))}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 5]} />
              <YAxis dataKey="name" type="category" width={150} />
              <SpanishTooltip />
              <Legend />
              <Bar dataKey="valor" name="Puntaje Ponderado" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              <Bar dataKey="promedio" name="Promedio Directo" fill="#94a3b8" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
